/**
 * Tooltip text for the "Active Request" badge on a shift that has an open replace request. Kept out
 * of `NurseShiftBadges` so each case is directly testable.
 */
import type { TFunction } from "i18next";
import { type DateTime, Settings } from "luxon";
import { type BaseRequest, NotificationType, type ReplaceShiftRequest, RequestResponsesStatusOptions } from "@/utils/models";

export type ActiveRequestTooltipInput = Pick<ReplaceShiftRequest, "request_type" | "auto_close_at" | "auto_close_uses_agency_terms"> & {
	agency_source_name?: string;
	message: Pick<BaseRequest["message"], "request_data">;
};

const AUTO_CLOSE_TIME_FORMAT: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };

const closeLine = (request: ActiveRequestTooltipInput, time: string, t: TFunction): string => {
	const agency = request.agency_source_name;
	// T&T Bill is matched by agency name, so a request with no agency name always closes on the 4-hour rule.
	if (!agency) return t("badge.active.request.close.default.unnamed", { time });
	if (request.auto_close_uses_agency_terms) return t("badge.active.request.close.agency", { time, agency });
	return t("badge.active.request.close.default", { time, agency });
};

export const activeRequestTooltip = (request: ActiveRequestTooltipInput | undefined, timezone: string, t: TFunction, now: DateTime): string => {
	const plain = t("badge.active.request.tooltip");
	// DEFAULT: no request raised for this shift, a staff replace request, or a department without auto-close
	// all have no close time to show, so they get the generic text.
	if (request?.request_type !== NotificationType.ReplaceAgency || !request.auto_close_at) return plain;

	if (request.auto_close_at <= now) {
		// Past its close time, only a pending pickup keeps it open. Without one it closes on the next sweep.
		const hasPendingPickup = request.message.request_data.some((data) => data.status === RequestResponsesStatusOptions.PENDING);
		return hasPendingPickup ? t("badge.active.request.close.held") : plain;
	}

	// A loaded request outlives a language switch, and a DateTime keeps the locale it was created with.
	const time = request.auto_close_at.setZone(timezone).setLocale(Settings.defaultLocale).toLocaleString(AUTO_CLOSE_TIME_FORMAT);
	return closeLine(request, time, t);
};
